/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, DoCheck,
  EventEmitter, forwardRef,
  Input, OnChanges, OnDestroy,
  OnInit, Output, SimpleChanges,
  ViewChild, ViewEncapsulation
} from '@angular/core';
import {MatSelect} from '@angular/material/select';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {debounceTime, map, switchMapTo, takeUntil} from 'rxjs/operators';
import {MessageService} from '../../services/message-service.service';
import {ErrorStateMatcher} from '@angular/material/core';
import isEmpty from 'lodash/isEmpty';
import {MAT_INPUT_VALUE_ACCESSOR} from '@angular/material/input';

class CustomFieldErrorMatcher implements ErrorStateMatcher {
  constructor(private customControl: FormControl) {}
  isErrorState(): boolean {
    return this.customControl && this.customControl.touched && this.customControl.invalid;
  }
}

// tslint:disable-next-line:no-conflicting-lifecycle
@Component({
  selector: 'app-input-search-dropdown',
  templateUrl: './input-search-dropdown.component.html',
  styleUrls: ['./input-search-dropdown.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputSearchDropdownComponent),
      multi: true
    },
    {provide: MAT_INPUT_VALUE_ACCESSOR, useExisting: InputSearchDropdownComponent}
  ]
})
export class InputSearchDropdownComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges, DoCheck {
  @Input() public placeholder: string;
  @Input() public testId: string;
  @Input() public inputLabel?: string;
  @Input() public inputClass?: string;
  @Input() inputType? = 'text';
  // tslint:disable-next-line:no-input-rename
  @Input('formControl') public resultControl: FormControl;
  @Input() public itemsList: any[];
  @Input() public itemLabel: string;
  @Input() public itemLabelFn?: (optionValue: any) => string;
  @Input() public itemFilterFn?: (optionValue: any) => any;
  @Input() public isLoading: Observable<boolean>;
  @Input() public inputHint?: string;
  @Input() public inputHintIcon?: boolean;
  @Input() highlights = false;

  @ViewChild('ItemsSelect', {static: true}) ItemsSelect: MatSelect;

  // tslint:disable-next-line:no-output-on-prefix
  @Output() private onSearch: EventEmitter<string> = new EventEmitter<string>();
  @Output() private onClear: EventEmitter<void> = new EventEmitter<void>();

  @Input()
  get isRequired(): boolean {
    return this._required;
  }
  set isRequired(value: boolean) {
    this._required = value;
  }

  // tslint:disable-next-line:variable-name
  private _required: boolean;
  private isDisabled: boolean;

  public inputCtrl: FormControl = new FormControl();
  public ItemsFormCtrl: FormControl = new FormControl();
  public ItemsFormFilterCtrl: FormControl = new FormControl();
  public filteredItemsList: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  public searching = false;

  /** Subject that emits when the component has been destroyed. */
  // tslint:disable-next-line:variable-name
  private _onDestroy = new Subject<void>();
  private oldTouched = null;

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private messageService: MessageService, private cd: ChangeDetectorRef) {}

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.ItemsFormCtrl.disable();
      this.inputCtrl.disable();
    } else {
      this.ItemsFormCtrl.enable();
      this.inputCtrl.enable();
    }
    this.cd.markForCheck();
  }

  ngOnInit() {
    // set initial selection
    this.ItemsFormCtrl.setValue([]);

    // load the initial items list
    this.filteredItemsList.next(this.itemsList.slice());

    // listen for search field value changes
    this.ItemsFormFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .pipe(debounceTime(1000))
      .pipe(
        map(search => {
          if (search.length) {
            this.searching = true;
            this.onSearch.emit(search);
          }
        }),
        switchMapTo(this.isLoading)
      )
      .subscribe(loading => {
        // this.onSearch.emit(value)
        this.filteredItemsList.next(this.itemsList);
        if (!loading) {
          this.searching = false;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.resultControl?.currentValue?.value) {
      this.inputCtrl.setValue(this.getOptionLabel(this.resultControl.value));
    }

    if (changes.resultControl) {
      if (this.resultControl.disabled) {
        this.inputCtrl.disable();
      } else {
        this.inputCtrl.enable();
      }
    }
  }

  ngDoCheck(): void {
    if (this.resultControl.touched !== this.oldTouched) {
      this.oldTouched = this.resultControl.touched;
      this.cd.markForCheck();
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  public openedChange(opened: boolean) {
    if (!opened && !isEmpty(this.ItemsFormCtrl.value)) {
      // Set value to parent control
      if (!isEmpty(this.ItemsFormCtrl.value)) {
        this.resultControl.setValue(this.ItemsFormCtrl.value);
      }
      // set value to inner input control
      if (this.resultControl.value) {
        this.inputCtrl.setValue(this.getOptionLabel(this.resultControl.value));
      } else {
        this.inputCtrl.setValue(this.resultControl.value);
      }

      // reset selected from dropdown value
      this.ItemsFormCtrl.reset();
    }

    if (!this.resultControl.touched) {
      this.resultControl.markAsTouched();
    }
  }

  public errorMatcher() {
    return new CustomFieldErrorMatcher(this.resultControl);
  }

  public getErrorMessage(): string {
    if (this.resultControl && this.resultControl.errors) {
      return this.messageService.getControlErrorMessage(this.resultControl.errors);
    }
    return null;
  }

  public getOptionLabel(item): any {
    if (!this.itemLabelFn) {
      return item && item[this.itemLabel] ? item[this.itemLabel] : '';
    }
    return this.itemLabelFn(item);
  }

  public clearInputValue(): void {
    if (!this.isDisabled) {
      this.resultControl.reset();
      this.inputCtrl.reset();
      this.onClear.next();
    }
  }

  public togglePanel(event: Event): void {
    if ((event.target as HTMLSpanElement).dataset.testId === `clear_button_${this.testId}`) {
      this.clearInputValue();
    } else {
      this.ItemsSelect.toggle();
    }
  }

  writeValue(value: any): void {}

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  highlighted(content: string, search: string) {
    if(search.length > 0) {
      // eslint-disable-next-line no-useless-escape
      const regex = new RegExp(search.replace(/[-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&'), 'gi');
      return '' + content.replace(regex, '<b>$&</b>');
    }
    return content;
  }

  withExtras(originalContent: string, item: {extras: string[]}, search) {
    const prefilteredExtras = (item.extras || []).filter(
      e =>
        e
          .replace(/(<([^>]+)>)/gi, '')
          .toLowerCase()
          .indexOf(search) > -1
    );
    if (prefilteredExtras.length) {
      return `${originalContent} (${prefilteredExtras.join(', ')})`;
    }
    return originalContent;
  }
}
